import React, { useEffect, useState } from 'react';
import { Modal, Spinner, Button } from 'react-bootstrap';
import axios from 'axios';
import { getAuthToken } from '../utils/commonMethods';
import { getUserFileViewAPI } from '../helpers/users/profile/personalDetails/ProfileAPI';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
const OFFICE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];

function getFileExtension(doc) {
  const name = doc.name || doc.file_name || '';
  if (name.includes('.')) {
    return name.split('.').pop().toLowerCase();
  }

  const url = doc.file || doc.link || '';
  const fromUrl = url.split('/').pop()?.split('?')[0] || '';
  if (fromUrl.includes('.')) {
    return fromUrl.split('.').pop().toLowerCase();
  }

  return (doc.fileType || '').replace(/^\./, '').toLowerCase();
}

function UserDocumentLink({ doc, className, style }) {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  const displayName =
    doc.name ||
    doc.file_name ||
    (doc.file && doc.file.split('/').slice(-1)[0].split('?')[0]) ||
    (doc.link && doc.link.split('/').slice(-1)[0].split('?')[0]) ||
    'Document';
  const fileUrl = doc.file || doc.link || '';
  const extension = getFileExtension(doc);
  const isOffice = OFFICE_EXTENSIONS.includes(extension);
  const isPdf = extension === 'pdf';
  const isImage = IMAGE_EXTENSIONS.includes(extension);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const clearPreviewUrl = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  const handleOpen = async (event) => {
    event.preventDefault();
    setShow(true);
    setError(null);
    clearPreviewUrl();

    if (isOffice && fileUrl) {
      setPreviewUrl(
        `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`
      );
      return;
    }

    if (!doc.id) {
      setError('Cannot preview this file. Use Download instead.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(getUserFileViewAPI(doc.id), {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (err) {
      const message =
        err.response?.data?.msg ||
        err.response?.data?.message ||
        'Unable to open this file.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShow(false);
    clearPreviewUrl();
    setError(null);
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <div className="user-document-preview-loading">
          <Spinner animation="border" role="status" size="sm" />
        </div>
      );
    }

    if (error) {
      return <p className="user-document-preview-error">{error}</p>;
    }

    if (!previewUrl) {
      return null;
    }

    if (isOffice || isPdf) {
      return (
        <iframe
          title={displayName}
          src={previewUrl}
          className="user-document-preview-frame"
        />
      );
    }

    if (isImage) {
      return (
        <div className="user-document-preview-image-wrap">
          <img
            src={previewUrl}
            alt={displayName}
            className="user-document-preview-image"
          />
        </div>
      );
    }

    return (
      <iframe
        title={displayName}
        src={previewUrl}
        className="user-document-preview-frame"
      />
    );
  };

  return (
    <>
      <a
        href="#"
        onClick={handleOpen}
        className={className}
        style={{ color: '#AA007F', cursor: 'pointer', ...style }}
      >
        {displayName}
      </a>

      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        dialogClassName="user-document-preview-modal"
      >
        <Modal.Header closeButton className="user-document-preview-header">
          <Modal.Title className="user-document-preview-title">{displayName}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="user-document-preview-body">
          {renderPreview()}
        </Modal.Body>
        <Modal.Footer className="user-document-preview-footer">
          {fileUrl ? (
            <Button
              variant="outline-secondary"
              size="sm"
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              as="a"
            >
              Download
            </Button>
          ) : null}
          <Button variant="primary" size="sm" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default UserDocumentLink;
